import type { PaginatedResponse } from "@common/types/api";
import type {
  FetchHarvestsParams,
  Harvest,
  HarvestCropTotal,
  HarvestsFilterParams,
  HarvestsSummary,
  HarvestsWeeklyTotal
} from "@common/types/Harvest";
import type { CurrencyPreference } from "@common/types/User";

import type { HttpTransport } from "./transport";

export type CreateHarvestParams = {
  gardenId?: number;
  harvestedOn: string;
  notes?: string;
  currency: CurrencyPreference;
  records: {
    gardenCropId: number;
    amountGrams: number;
    valuePerGram?: number;
    saveAsDefault?: boolean;
  }[];
};

export type UpdateHarvestParams = {
  id: number;
  harvestedOn?: string;
  notes?: string;
  totalAmountGrams?: number;
};

export type UpdateHarvestRecordParams = {
  harvestId: number;
  recordId: number;
  amountGrams?: number;
  valuePerGram?: number | null;
  currency?: CurrencyPreference;
};

export type HarvestRecordRef = { harvestId: number; recordId: number };

export type DestroyHarvestRecordResponse = { harvest: Harvest | null };

const harvestRecordUrl = ({ harvestId, recordId }: HarvestRecordRef) =>
  `/api/harvests/${harvestId}/records/${recordId}`;

export const createHarvestsApi = (http: HttpTransport) => ({
  bulkDestroyHarvests: (ids: number[]) =>
    http.del<{ status: number }>("/api/harvests/bulk_destroy", { ids }),

  createHarvest: (harvest: CreateHarvestParams) =>
    http.post<Harvest>("/api/harvests", { harvest }),

  destroyHarvest: (id: number) => http.del<{ status: number }>(`/api/harvests/${id}`, {}),

  destroyHarvestRecord: (ref: HarvestRecordRef) =>
    http
      .del<DestroyHarvestRecordResponse>(harvestRecordUrl(ref), {})
      .then(({ harvest }) => harvest),

  fetchHarvests: (params: FetchHarvestsParams = {}) =>
    http.get<PaginatedResponse<Harvest>>("/api/harvests", { queryParams: params }),

  fetchHarvestsCropTotals: (params: HarvestsFilterParams = {}) =>
    http.get<HarvestCropTotal[]>("/api/harvests/crop_totals", { queryParams: params }),

  fetchHarvestsSummary: (params: HarvestsFilterParams = {}) =>
    http.get<HarvestsSummary>("/api/harvests/summary", { queryParams: params }),

  fetchHarvestsWeeklyTotals: (params: HarvestsFilterParams = {}) =>
    http.get<HarvestsWeeklyTotal[]>("/api/harvests/weekly_totals", {
      queryParams: params
    }),

  updateHarvest: ({ id, ...params }: UpdateHarvestParams) =>
    http.patch<Harvest>(`/api/harvests/${id}`, { harvest: params }),

  updateHarvestRecord: ({ harvestId, recordId, ...params }: UpdateHarvestRecordParams) =>
    http.patch<Harvest>(harvestRecordUrl({ harvestId, recordId }), { record: params })
});
